import { BaseApiService, baseApiService } from './base'
import { PaginatedResponse } from './types'

export interface FolderType {
  id: number
  name: string
  description?: string
  color?: string
  icon?: string
  isSystemType: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  usageCount?: number
  permissions?: {
    canEdit: boolean
    canDelete: boolean
  }
}

export interface FolderTypeRequest {
  name: string
  description?: string
  color?: string
  icon?: string
  isActive?: boolean
  [key: string]: unknown
}

export interface FolderTypeUpdateRequest extends Partial<FolderTypeRequest> {
  id: number
}

export interface FolderTypePickerOptions {
  includeSystemTypes?: boolean
  includeInactive?: boolean
  sortBy?: string
  sortDesc?: boolean
}

export interface FolderTypeUsageResponse {
  folderTypeId: number
  usageCount: number
  lastUsedAt?: string
  canDelete: boolean
}

export interface FolderTypeSearchOptions {
  query?: string
  includeSystemTypes?: boolean
  includeInactive?: boolean
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | undefined
}

export type FolderTypesResponse = PaginatedResponse<FolderType>

class FolderTypesService extends BaseApiService {
  private readonly basePath = '/api/foldertypetemplates'

  async getFolderTypes(options: FolderTypeSearchOptions = {}): Promise<FolderTypesResponse> {
    return this.fetch<FolderTypesResponse>(this.basePath, {
      params: options
    })
  }

  async getFolderType(id: number): Promise<FolderType> {
    return this.fetch<FolderType>(`${this.basePath}/${id}`)
  }

  async createFolderType(folderType: FolderTypeRequest): Promise<FolderType> {
    return this.fetch<FolderType>(this.basePath, {
      method: 'POST',
      body: folderType
    })
  }

  async updateFolderType(folderType: FolderTypeUpdateRequest): Promise<FolderType> {
    const { id, ...updateData } = folderType
    return this.fetch<FolderType>(`${this.basePath}/${id}`, {
      method: 'PUT',
      body: updateData
    })
  }

  async deleteFolderType(id: number): Promise<void> {
    return this.fetch<void>(`${this.basePath}/${id}`, {
      method: 'DELETE'
    })
  }

  async getFolderTypesForPicker(
    options: FolderTypePickerOptions = {}
  ): Promise<Array<{ id: number; name: string; color?: string; icon?: string }>> {
    const searchOptions: FolderTypeSearchOptions = {
      includeSystemTypes: options.includeSystemTypes,
      includeInactive: options.includeInactive,
      sortBy: options.sortBy ?? 'name',
      sortDesc: options.sortDesc ?? false,
      pageSize: 1000 // Get all for picker
    }

    const response = await this.getFolderTypes(searchOptions)

    return response.elements.map((folderType) => ({
      id: folderType.id,
      name: folderType.name,
      color: folderType.color,
      icon: folderType.icon
    }))
  }

  async searchFolderTypes(
    query: string,
    options: Omit<FolderTypeSearchOptions, 'query'> = {}
  ): Promise<FolderTypesResponse> {
    return this.getFolderTypes({
      ...options,
      query
    })
  }

  async getFolderTypeUsage(id: number): Promise<FolderTypeUsageResponse> {
    return this.fetch<FolderTypeUsageResponse>(`${this.basePath}/${id}/usage`)
  }

  async canDeleteFolderType(id: number): Promise<boolean> {
    try {
      const usage = await this.getFolderTypeUsage(id)
      return usage.canDelete
    } catch (error) {
      console.error('Error checking folder type deletion status:', error)
      return false
    }
  }

  async bulkDeleteFolderTypes(ids: number[]): Promise<{ deleted: number[]; failed: number[] }> {
    const results = await Promise.allSettled(ids.map((id) => this.deleteFolderType(id).then(() => id)))

    const deleted: number[] = []
    const failed: number[] = []

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        deleted.push(result.value)
      } else {
        failed.push(ids[index])
      }
    })

    return { deleted, failed }
  }

  /**
   * Strict migration of legacy getAllFolderTypes (api.foldertypes.js)
   * @param options Optional search/sort params
   * @returns Promise<FolderTypesResponse>
   */
  async getAllFolderTypes(options?: FolderTypeSearchOptions): Promise<FolderTypesResponse> {
    // Match legacy: default sortBy 'name', sortDesc false
    const params = options ?? { sortBy: 'name', sortDesc: false }
    return this.fetch<FolderTypesResponse>(this.basePath, {
      params
    })
  }
}

export const folderTypesService = new FolderTypesService()

/**
 * Get all folder types (legacy API compatibility)
 * Maps to the legacy getAllFolderTypes method from api.foldertypes.js
 * @param params - Query parameters for filtering and sorting
 * @returns List of folder types
 */
export async function getAll(params?: { sortBy?: string; sortDesc?: boolean }): Promise<any> {
  const defaultParams = { sortBy: 'name', sortDesc: false }
  const queryParams = params ? params : defaultParams
  return baseApiService.fetch<any>('/api/foldertypetemplates', {
    params: queryParams
  })
}
